import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Box, Text } from "@mantine/core";
import { useMatches } from "../../api/queries";
import type { TimelineBlock, TrackDetection } from "../../api/types";
import { formatDateTime, formatDuration } from "../../time/format";
import { alpha, token } from "../../ui/tokens";
import { liveEndMs } from "../geometry";
import { blockLabel } from "../label";

/** Where the pointer is, and the block it is over: everything the hover box needs. */
export interface Tooltip {
  x: number;
  y: number;
  block: TimelineBlock;
}

/** Gap between the pointer and the tooltip, in pixels. */
const TOOLTIP_GAP_PX = 12;

/**
 * Where a tooltip fits, measured rather than assumed.
 *
 * Placed to the right of the pointer, and flipped to its left when that would run past the
 * canvas -- an absolutely positioned box hanging over the edge widens the page and puts a
 * horizontal scrollbar under the whole layout. Measurement rather than a guessed width,
 * because the content is a title of any length.
 *
 * `content` is whatever else the box is waiting on -- the match rows, which arrive after the
 * first paint and make it taller. Measuring once would leave the box placed for the height it
 * used to have, which on a tooltip anchored above the pointer means placed over it.
 */
function useTooltipPlacement(tooltip: Tooltip, content?: unknown) {
  const ref = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    const parent = el?.offsetParent as HTMLElement | null;
    if (!el || !parent) return;
    const { width, height } = el.getBoundingClientRect();
    const room = parent.clientWidth;
    const fitsRight = tooltip.x + TOOLTIP_GAP_PX + width <= room;
    // Where the box would sit resting on the lane's own bottom edge. Negative when it is
    // taller than the lane, which the match rows can make it.
    const floor = parent.clientHeight - height;
    setPlacement({
      left: fitsRight
        ? tooltip.x + TOOLTIP_GAP_PX
        : Math.max(0, tooltip.x - TOOLTIP_GAP_PX - width),
      // Above the pointer, and never above the canvas: the toolbar sits directly on top.
      // The one exception is a box that does not fit in the lane at all -- pinned inside it,
      // it hangs off the bottom of the canvas, which is the last thing on the page, and so
      // off the screen. Then it rises by exactly the overflow and no further.
      top: Math.max(Math.min(0, floor), Math.min(tooltip.y - height - TOOLTIP_GAP_PX, floor)),
    });
  }, [tooltip, content]);

  return { ref, placement };
}

/** The three states the tracker under a show block reports, in words. */
function listenedLabel(pct: number, t: TFunction): string {
  if (pct === 0) return t("timeline.notListened");
  if (pct === 100) return t("timeline.listened");
  return t("timeline.listenedPct", { pct });
}

/** "Artist - Title" for one detection, the way the track it was promoted into writes itself. */
function detectionLabel(detection: TrackDetection, t: TFunction): string {
  return (
    blockLabel(
      {
        title: detection.title,
        artist: detection.artist,
        djs: null,
        city: null,
        marker: null,
      },
      t,
    ) ?? t("timeline.unknown")
  );
}

export function BlockTooltip({ tooltip, zoneName }: { tooltip: Tooltip; zoneName: string }) {
  const { block } = tooltip;
  const { t } = useTranslation();
  const startMs = Date.parse(block.start);
  // The same end the block is drawn to, so the duration cannot report a running show two
  // minutes shorter than the block it is describing.
  const endMs = liveEndMs(block, Date.now());
  // Only for a matched track, and only for the one under the pointer. A matched track draws
  // nothing of its own, so this is the only place the score behind it is ever said.
  const { data: heard } = useMatches(block.id, block.matched);
  // The detection this block was named after, and the records that lost to it over the same
  // stretch. `promoted_segment_id` rather than simply the top of a score-ordered list: two
  // records can both clear the threshold on one stretch, and then each is the other's
  // runner-up and only this column says which one is the block being pointed at.
  const mine = heard?.find((d) => d.promoted_segment_id === block.id) ?? heard?.[0];
  const alsoHeard = heard?.filter((d) => d !== mine) ?? [];
  const { ref, placement } = useTooltipPlacement(tooltip, heard);
  return (
    // A `Box`, not a Mantine `Tooltip`: that anchors to an element, and this one follows a
    // point inside the canvas -- there is no element under the pointer to attach it to.
    <Box
      ref={ref}
      role="tooltip"
      aria-hidden="true"
      pos="absolute"
      px={9}
      py={6}
      fz={12}
      c="dimmed"
      bg={alpha(token.bgSunken, 0.96)}
      bd="1px solid var(--mantine-color-default-border)"
      // Narrower than the canvas on a phone, so a long title wraps rather than deciding there
      // is no side of the pointer it fits on.
      maw="min(320px, 100%)"
      style={{
        // No transform offset: the left/top are measured against the canvas by the component,
        // so that a tooltip near the right edge flips to the other side of the pointer instead
        // of hanging over the edge and widening the page.
        left: placement?.left ?? tooltip.x,
        top: placement?.top ?? tooltip.y,
        // The first paint is what gets measured, so it must not be seen at the unplaced
        // position; hidden rather than unmounted, or there would be nothing to measure.
        visibility: placement ? undefined : "hidden",
        pointerEvents: "none",
        borderRadius: "var(--mantine-radius-sm)",
        display: "grid",
        gap: 2,
        whiteSpace: "nowrap",
        zIndex: 5,
      }}
    >
      <Text component="strong" fz="inherit" fw={700} truncate c="var(--mantine-color-text)">
        {block.count > 1
          ? t("timeline.trackCount", { count: block.count })
          : (blockLabel(block, t) ?? t("timeline.untitled"))}
      </Text>
      <span>{formatDateTime(startMs, zoneName)}</span>
      <span>{formatDuration((endMs - startMs) / 1000)}</span>
      {block.kind === "show" && block.on_air && (
        <Text component="span" fz="inherit" fw={700} c="danger">
          {t("timeline.flagLive")}
        </Text>
      )}
      {block.state === "provisional" && block.kind === "show" && !block.on_air && (
        <Text component="span" fz="inherit" c="warn">
          {t("timeline.flagUnverified")}
        </Text>
      )}
      {!block.playable && (
        <Text component="span" fz="inherit" c="warn">
          {t("timeline.flagExpired")}
        </Text>
      )}
      {block.kind === "show" && block.listened_pct !== null && (
        <Text component="span" fz="inherit" c="unheard">
          {listenedLabel(block.listened_pct, t)}
        </Text>
      )}
      {/* Panako's score, not a percentage: it is matching hashes summed over the windows the
          play was welded out of, it has no ceiling, and it grows with the length of the
          record. What it is good for is the comparison directly underneath it. */}
      {block.matched && (
        <span>
          {mine
            ? t("timeline.matchedScore", { score: Math.round(mine.score) })
            : t("timeline.matched")}
        </span>
      )}
      {alsoHeard.length > 0 && <span>{t("timeline.alsoHeard")}</span>}
      {/* A record the fingerprinter also heard over a matched track's stretch. Ellipsised
          like the heading is: it is a record name, and the tooltip is nowrap, so a long one
          would push the box straight past the max-width that keeps it on the canvas. */}
      {alsoHeard.map((detection) => (
        <Text component="span" fz="inherit" truncate key={detection.id}>
          {t("timeline.altScore", {
            label: detectionLabel(detection, t),
            score: Math.round(detection.score),
          })}
        </Text>
      ))}
    </Box>
  );
}
